import { AxiosError } from 'axios';

import { proxyAxios } from 'api/apiBase/axios';
import { mapError } from 'api/apiBase/mapError';
import { mapResponse } from 'api/apiBase/mapResponse';
import type { Response } from 'api/apiBase/Response';

import { GeckoterminalToken } from '../types';

import { GetGeckoterminalTokenRequest } from './api.types';

/** GeckoTerminal rate-limit code. The proxy can send it in the body of an HTTP 200 answer. */
const GECKOTERMINAL_RATE_LIMIT_CODE = 429;

/** Pauses before the second and the third request after a 429 answer. */
const GECKOTERMINAL_RATE_LIMIT_RETRY_DELAYS_MS: readonly number[] = [1000, 2000];

type GeckoterminalTokenBody = {
  data?: GeckoterminalToken;
  status?: { error_code?: unknown; error_message?: unknown };
};

/**
 * Page-lifetime request cache: one GeckoTerminal request per token and page load.
 * The cache keeps the in-flight promise, so parallel callers share one request.
 * The cache removes a failed result. The next call sends a new request.
 */
const geckoterminalTokenCache = new Map<string, Promise<Response<GeckoterminalToken>>>();

const geckoterminalTokenCacheKey = (req: GetGeckoterminalTokenRequest) => {
  // EVM addresses are case-insensitive. Solana mints are case-sensitive.
  const address = req.tokenAddress.startsWith('0x')
    ? req.tokenAddress.toLowerCase()
    : req.tokenAddress;
  return `${req.network}:${address}:${req.include ?? ''}`;
};

/** Clears the request cache. Tests use it to isolate cases. */
export function resetGeckoterminalTokenCache() {
  geckoterminalTokenCache.clear();
}

/**
 * Returns `status.error_code` of a proxied GeckoTerminal body, or `undefined`.
 * On a rate limit the proxy sends HTTP 200 with the body `{"status":{"error_code":429,…}}`.
 */
const geckoterminalBodyErrorCode = (body: GeckoterminalTokenBody | undefined) => {
  const status = body?.status;
  if (!status || typeof status !== 'object') return undefined;
  const code = Number(status.error_code);
  return Number.isInteger(code) && code > 0 ? code : undefined;
};

const geckoterminalBodyError = (
  body: GeckoterminalTokenBody,
  errorCode: number,
): Response<GeckoterminalToken> => {
  const message = body.status?.error_message;
  return {
    isSuccess: false,
    errorCode,
    errorMessage:
      typeof message === 'string' && message ? message : `GeckoTerminal error ${errorCode}`,
  };
};

async function requestGeckoterminalToken(
  req: GetGeckoterminalTokenRequest,
): Promise<Response<GeckoterminalToken>> {
  try {
    const url = new URL(
      `https://api.geckoterminal.com/api/v2/networks/${req.network}/tokens/${req.tokenAddress}`,
    );
    const params = new URLSearchParams();

    if (req.include) {
      params.append('include', req.include);
    }

    url.search = params.toString();

    const response = await proxyAxios.get<GeckoterminalTokenBody>('/api/v1/proxy', {
      params: {
        url: url.toString(),
      },
    });

    const body = response.data;
    const bodyErrorCode = geckoterminalBodyErrorCode(body);
    if (bodyErrorCode !== undefined) return geckoterminalBodyError(body, bodyErrorCode);
    if (!body?.data) {
      return { isSuccess: false, errorCode: 0, errorMessage: 'GeckoTerminal sent no token data' };
    }

    return mapResponse<GeckoterminalToken>({ data: body.data });
  } catch (error) {
    // axios 0.27 keeps the HTTP status only in `response.status`.
    if (error instanceof AxiosError && error.response?.status === GECKOTERMINAL_RATE_LIMIT_CODE) {
      return {
        isSuccess: false,
        errorCode: GECKOTERMINAL_RATE_LIMIT_CODE,
        errorMessage: 'GeckoTerminal rate limit',
      };
    }
    return mapError<GeckoterminalToken>({ error });
  }
}

const isRateLimited = (result: Response<GeckoterminalToken>) =>
  !result.isSuccess && result.errorCode === GECKOTERMINAL_RATE_LIMIT_CODE;

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Sends the request again after a 429 answer: 2 retries at most, with pauses of 1 s and 2 s.
 * After the third 429 answer the function returns the error.
 */
async function requestGeckoterminalTokenWithRetry(
  req: GetGeckoterminalTokenRequest,
): Promise<Response<GeckoterminalToken>> {
  let result = await requestGeckoterminalToken(req);
  for (const delayMs of GECKOTERMINAL_RATE_LIMIT_RETRY_DELAYS_MS) {
    if (!isRateLimited(result)) return result;
    await wait(delayMs);
    result = await requestGeckoterminalToken(req);
  }
  return result;
}

export function getGeckoterminalToken(
  req: GetGeckoterminalTokenRequest,
): Promise<Response<GeckoterminalToken>> {
  const key = geckoterminalTokenCacheKey(req);
  const cached = geckoterminalTokenCache.get(key);
  if (cached) return cached;

  const request = requestGeckoterminalTokenWithRetry(req);
  geckoterminalTokenCache.set(key, request);

  // The cache keeps only a successful result. A reset or a newer request can own the key.
  const evict = () => {
    if (geckoterminalTokenCache.get(key) === request) geckoterminalTokenCache.delete(key);
  };
  request.then(result => {
    if (!result.isSuccess) evict();
  }, evict);

  return request;
}
